import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { defer, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class SharedReportArtifactService {
  constructor(private readonly http: HttpClient) {}

  convertImageUrlToDataUri(source: string): Observable<string> {
    return defer(() => {
      if (typeof source !== 'string' || !source.trim()) {
        throw new Error('An image source is required.');
      }
      if (/^data:/i.test(source)) {
        return of(this.validateDataUri(source));
      }
      this.validateUrl(source);
      return this.http.get(source, { responseType: 'blob' }).pipe(
        switchMap(blob => {
          if (!this.isSupportedMime(blob.type) || !blob.size) {
            throw new Error('The image response must be a nonempty PNG, JPEG, GIF or WebP.');
          }
          return this.readBlob(blob);
        })
      );
    });
  }

  private isSupportedMime(mime: string): boolean {
    return /^image\/(?:png|jpeg|jpg|gif|webp)$/i.test(mime);
  }

  private validateDataUri(source: string): string {
    const match = /^data:([^;,]+)(;base64)?,(.+)$/is.exec(source);
    if (!match || !this.isSupportedMime(match[1])) {
      throw new Error('Unsupported or malformed embedded image.');
    }
    const payload = match[3];
    if (match[2]) {
      if (!/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}(?:==)?|[A-Za-z0-9+/]{3}=?)?$/.test(payload)
        || !payload.length) {
        throw new Error('Malformed base64 image.');
      }
      // Reject noncanonical padding bits as well as invalid base64 characters.
      if (btoa(atob(payload)).replace(/=+$/, '') !== payload.replace(/=+$/, '')) {
        throw new Error('Malformed base64 image.');
      }
    } else {
      if (/%(?![a-f\d]{2})/i.test(payload) || /[\s\u0000-\u001f\u007f]/.test(payload)) {
        throw new Error('Malformed encoded image.');
      }
    }
    return source;
  }

  private validateUrl(source: string): void {
    if (/[\s\\\u0000-\u001f\u007f]/.test(source) || /%(?![a-f\d]{2})/i.test(source)) {
      throw new Error('Malformed image URL.');
    }
    if (/^\/(?!\/)/.test(source)) {
      return;
    }
    if (!/^https?:\/\/[^/]/i.test(source)) {
      throw new Error('Image URLs must be root-relative or HTTP(S).');
    }
    const url = new URL(source);
    if (!url.hostname || url.username || url.password) {
      throw new Error('Malformed image URL.');
    }
  }

  private readBlob(blob: Blob): Observable<string> {
    return new Observable<string>(subscriber => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== 'string') {
          subscriber.error(new Error('The image reader did not return a data URI.'));
          return;
        }
        subscriber.next(reader.result);
        subscriber.complete();
      };
      reader.onerror = () => subscriber.error(reader.error ?? new Error('Image reading failed.'));
      reader.onabort = () => subscriber.error(new Error('Image reading was aborted.'));
      try {
        reader.readAsDataURL(blob);
      } catch (error: unknown) {
        subscriber.error(error);
      }
      return () => {
        reader.onload = null;
        reader.onerror = null;
        reader.onabort = null;
        if (reader.readyState === FileReader.LOADING) {
          reader.abort();
        }
      };
    }).pipe(map(result => this.validateDataUri(result)));
  }
}
